import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Users, Eye, ShoppingCart, CreditCard, MessageCircle, ShoppingBag, AlertCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PERIODS = [
  { key: 'today', label: 'Today' },
  { key: '7d', label: '7 Days' },
  { key: '30d', label: '30 Days' },
  { key: '90d', label: '3 Months' },
  { key: '365d', label: '1 Year' },
  { key: 'all', label: 'All Time' },
];

const Tile = ({ icon, bg, label, value, testid }) => (
  <Card className="bg-white border border-[#F0EDE5] p-3" data-testid={testid}>
    <div className="flex items-center gap-2">
      <div className={`${bg} p-2 rounded-full`}>{icon}</div>
      <div className="min-w-0">
        <p className="text-[10px] text-[#8B7D6B] font-montserrat uppercase tracking-wider">{label}</p>
        <p className="text-lg font-seasons text-[#2C2C2C] truncate">{value ?? 0}</p>
      </div>
    </div>
  </Card>
);

export const WebsiteAnalyticsPanel = () => {
  const [period, setPeriod] = useState('7d');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = async (p) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('adminToken');
      const res = await axios.get(`${API}/admin/analytics/website?period=${p}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(res.data);
    } catch (e) {
      toast.error('Failed to load website analytics');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(period); }, [period]);

  const s = data?.summary || {};
  const top = data?.top_products || [];
  const abandoned = data?.abandoned_carts || [];

  return (
    <Card className="bg-white border-none shadow-lg p-6" data-testid="website-analytics-panel">
      <div className="flex flex-wrap justify-between items-center gap-3 mb-6">
        <div>
          <h2 className="text-2xl font-seasons text-[#2C2C2C]">Website Analytics</h2>
          <p className="text-sm font-montserrat text-[#8B7D6B]">
            Visitors, product views, add-to-carts, and abandoned carts
          </p>
        </div>
        <div className="flex flex-wrap gap-2" data-testid="web-period-selector">
          {PERIODS.map(p => (
            <Button
              key={p.key}
              size="sm"
              variant={period === p.key ? 'default' : 'outline'}
              data-testid={`web-period-${p.key}`}
              onClick={() => setPeriod(p.key)}
              className={period === p.key ? 'bg-[#C4B5A0] hover:bg-[#B0A090] text-white' : 'border-[#E8E5E0] text-[#2C2C2C]'}
            >
              {p.label}
            </Button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3 mb-6">
        <Tile icon={<Users className="h-5 w-5 text-blue-600" />} bg="bg-blue-50" label="Visitors" value={s.visitors} testid="metric-visitors" />
        <Tile icon={<Eye className="h-5 w-5 text-[#C4B5A0]" />} bg="bg-[#F5F3F0]" label="Page Views" value={s.page_views} testid="metric-pageviews" />
        <Tile icon={<ShoppingCart className="h-5 w-5 text-orange-600" />} bg="bg-orange-50" label="Add to Cart" value={s.add_to_cart} testid="metric-atc" />
        <Tile icon={<CreditCard className="h-5 w-5 text-purple-600" />} bg="bg-purple-50" label="Checkouts" value={s.checkouts_started} testid="metric-checkouts" />
        <Tile icon={<ShoppingBag className="h-5 w-5 text-green-600" />} bg="bg-green-50" label="Purchases" value={s.purchases} testid="metric-purchases" />
        <Tile icon={<MessageCircle className="h-5 w-5 text-[#25D366]" />} bg="bg-green-50" label="WhatsApp" value={s.whatsapp_clicks} testid="metric-whatsapp" />
        <Tile icon={<AlertCircle className="h-5 w-5 text-red-600" />} bg="bg-red-50" label="Abandoned" value={s.abandoned_carts} testid="metric-abandoned" />
      </div>

      {loading && <p className="text-center text-sm text-[#8B7D6B]">Loading...</p>}

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Top viewed products */}
        <div className="bg-[#FAF9F7] rounded-lg p-4" data-testid="top-viewed-products">
          <h3 className="font-seasons text-lg text-[#2C2C2C] mb-3">Top Viewed Products</h3>
          {top.length === 0 ? (
            <p className="text-sm font-montserrat text-[#8B7D6B]">No product views recorded yet.</p>
          ) : (
            <div className="space-y-2">
              {top.map((p, i) => (
                <div key={p.product_id} className="flex justify-between items-center text-sm font-montserrat" data-testid={`top-viewed-row-${i}`}>
                  <span className="truncate pr-3 text-[#2C2C2C]">{i + 1}. {p.name || `#${p.product_id}`}</span>
                  <span className="text-[#8B7D6B] whitespace-nowrap">{p.views} views</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Abandoned carts */}
        <div className="bg-[#FAF9F7] rounded-lg p-4" data-testid="abandoned-carts-list">
          <h3 className="font-seasons text-lg text-[#2C2C2C] mb-3">Abandoned Carts</h3>
          {abandoned.length === 0 ? (
            <p className="text-sm font-montserrat text-[#8B7D6B]">No abandoned carts in this period.</p>
          ) : (
            <div className="space-y-3 max-h-80 overflow-y-auto">
              {abandoned.map((c, idx) => (
                <div key={`${c.session_id}-${idx}`} className="bg-white rounded p-3 border border-[#F0EDE5]" data-testid={`abandoned-row-${idx}`}>
                  <div className="flex justify-between items-start gap-2">
                    <div className="min-w-0">
                      <p className="text-sm font-montserrat text-[#2C2C2C] truncate">
                        {c.name || 'Anonymous'}{c.email ? ` · ${c.email}` : ''}
                      </p>
                      {c.phone && <p className="text-xs text-[#8B7D6B] font-montserrat">📱 {c.phone}</p>}
                    </div>
                    {typeof c.total_amount === 'number' && (
                      <span className="text-sm font-seasons text-[#2C2C2C] whitespace-nowrap">
                        ₹{c.total_amount.toLocaleString('en-IN')}
                      </span>
                    )}
                  </div>
                  {Array.isArray(c.items) && c.items.length > 0 && (
                    <p className="text-xs font-montserrat text-[#8B7D6B] mt-1 truncate">
                      {c.items.length} item{c.items.length > 1 ? 's' : ''}: {c.items.map(i => i.name).join(', ')}
                    </p>
                  )}
                  {c.updated_at && (
                    <p className="text-[10px] font-montserrat text-[#B0A090] mt-1">
                      {new Date(c.updated_at).toLocaleString('en-IN')}
                    </p>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </Card>
  );
};
